import {
  MixpanelEvents,
  MixpanelSystemActionTypes,
} from "@/modules/analytics/enums";
import {
  AnalyticsServiceProviders,
  CallSummaryClickedPayload,
  ChatsCallsSource,
  FiltersMemo,
  MixpanelOverviewClickableCardsEnum,
  MixpanelOverviewClickableTargetsEnum,
  PageViewOptions,
  QrCodeEventPayload,
  UserInfo,
} from "@/modules/analytics/types";
import { AnalyticsService } from "@/modules/analytics/services/analytics.service";
import { Lead } from "@/modules/leads/types";
import { Task } from "@/modules/tasks/dtos";

/* ----------------------------- Utility Class ----------------------------- */

/**
 * Utility class for Mixpanel event tracking.
 * Provides reusable methods for common tracking scenarios.
 */
export class MixpanelTrackingUtil {
  /**
   * Tracks filter application events.
   */
  static trackFiltersApplied(
    analytics: AnalyticsService | null,
    filtersMemo: FiltersMemo,
    pageType: string,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    const isValidFilterValue = (v: unknown): boolean =>
      v !== "" && v !== undefined && v !== null && v !== false;

    const entries = Object.entries(filtersMemo).filter(
      ([key, value]) =>
        Object.prototype.hasOwnProperty.call(filtersMemo, key) &&
        isValidFilterValue(value)
    );

    const appliedFilters = Object.fromEntries(entries);
    const filterKeys = entries.map(([key]) => key);

    const payload = {
      appliedFilters,
      filterKeys,
      totalFiltersCount: filterKeys.length,
      pageType,
      timestamp: new Date().toISOString(),
      userName: userInfo?.name,
      userRank: userInfo?.rank,
    };

    analytics.track({
      name: MixpanelEvents.LEADS_TABLE_FILTERS_APPLIED,
      customParams: payload,
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks lead navigation events.
   */
  static trackLeadNavigation(
    analytics: AnalyticsService | null,
    lead: Lead | null,
    source: string,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics || !lead) return;

    const usefulParams = {
      name: lead.name,
      nanoId: lead.nanoId,
      leadSource: lead.lead_source?.name,
      leadType: lead.lead_type?.lead_type,
      status: lead.status?.status,
      statusCategory: lead.status?.statusCategory?.name,
      assignedUser: lead.user?.name,
      createdAt: lead.created_at,
    };

    analytics.track({
      name: MixpanelEvents.LEAD_PAGE_NAVIGATION,
      customParams: {
        source,
        ...usefulParams,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks page view events.
   */
  static trackPageView(
    analytics: AnalyticsService | null,
    currentPage: string,
    previousPage: string | null,
    options?: PageViewOptions,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.pageView({
      name: MixpanelEvents.PAGE_VIEW,
      customParams: {
        currentPage,
        previousPage,
        hash: options?.hash,
        search: options?.search,
        fullUrl: options?.fullUrl,
        isInitialLoad: options?.isInitialLoad ?? false,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks click events on the target details modal toggle.
   */
  static trackOverviewTargetsClick(
    analytics: AnalyticsService | null,
    event: MixpanelOverviewClickableTargetsEnum,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.track({
      name: event,
      customParams: {
        event,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks click events on overview cards.
   */
  static trackOverviewCardClick(
    analytics: AnalyticsService | null,
    event: MixpanelOverviewClickableCardsEnum,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.track({
      name: event,
      customParams: {
        event,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks task completion events.
   */
  static trackTaskCompletion(
    analytics: AnalyticsService | null,
    task: Task | null,
    source: string,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics || !task) return;

    const usefulParams = {
      taskId: task.uid,
      taskType: task.type,
      taskTitle: task.title,
      taskStatus: task.status,
      assignedUser: task.User?.name,
      createdBy: task.CreatedBy?.name,
      dueDate: task.dueDate,
      hasLinkedEntities: task.linkedEntities && task.linkedEntities.length > 0,
      linkedEntities: task.linkedEntities || [],
    };

    analytics.track({
      name: MixpanelEvents.TASK_MARKED_AS_DONE,
      customParams: {
        source,
        ...usefulParams,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks generic system actions (button clicks, navigation, interactions, etc.).
   */
  static trackSystemAction(
    analytics: AnalyticsService | null,
    actionType: MixpanelSystemActionTypes,
    screen: string,
    userInfo?: UserInfo | null,
    additionalParams?: Record<string, any>
  ): void {
    if (!analytics) return;

    analytics.track({
      name: MixpanelEvents.USER_ACTION,
      customParams: {
        actionType,
        screen,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
        ...additionalParams,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks eRealty presentation form submission events.
   */
  static trackErealtyPresentationFiltersSubmit(
    analytics: AnalyticsService | null,
    payload: any,
    userInfo?: UserInfo | null,
    additionalParams?: {
      clicked_show_results?: boolean;
      time_between_show_results_and_submit_form?: number;
      status_update_uid?: string;
     form_submission_timestamp?: string;
    }
  ): void {
    if (!analytics) return;

    analytics.track({
      name: MixpanelEvents.EREALTY_PRESENTATION_FILTERS_SUBMIT,
      customParams: {
        payload,
        timestamp: new Date().toISOString(),
        userName: userInfo?.name,
        userRank: userInfo?.rank,
        ...additionalParams,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }
  static trackRegenerateQrCode(
    analytics: AnalyticsService | null,
    payload: QrCodeEventPayload,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.track({
      name: MixpanelEvents.EXPO_QR_REGENERATED,
      customParams: {
        ...payload,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  static trackSendQrCodeToClient(
    analytics: AnalyticsService | null,
    payload: QrCodeEventPayload,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.track({
      name: MixpanelEvents.EXPO_QR_SEND_TO_CLIENT,
      customParams: {
        ...payload,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks opening the lead-page "Chats & Calls" surface (desktop tab or mobile action card).
   */
  static trackChatsCallsTabClick(
    analytics: AnalyticsService | null,
    source: ChatsCallsSource,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.track({
      name: MixpanelEvents.CHATS_CALLS,
      customParams: {
        source,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }

  /**
   * Tracks opening a call / WhatsApp summary in the Chats & Calls tab, and each
   * Overview / Analysis / Transcript sub-tab switch inside a call summary.
   */
  static trackCallSummaryClicked(
    analytics: AnalyticsService | null,
    payload: CallSummaryClickedPayload,
    userInfo?: UserInfo | null
  ): void {
    if (!analytics) return;

    analytics.track({
      name: MixpanelEvents.CALL_SUMMARY_CLICKED,
      customParams: {
        ...payload,
        userName: userInfo?.name,
        userRank: userInfo?.rank,
      },
      services: [AnalyticsServiceProviders.MIXPANEL],
    });
  }
}
